// src/components/store/UserHeaderMenu.tsx
"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import {
  User as UserIcon,
  LogOut,
  LayoutDashboard,
  Sparkles,
  Globe,
  Check,
  ChevronDown,
  Smartphone,
  Apple,
  Monitor,
  Download,
} from "lucide-react";

type StoreLangCode = "en" | "bn" | "hi" | "ur" | "ar";

interface StoreLanguageOption {
  code: StoreLangCode;
  name: string;
  nativeName: string;
  badge: string;
  dir: "ltr" | "rtl";
}

const STORE_LANGUAGES: StoreLanguageOption[] = [
  { code: "en", name: "English", nativeName: "English (Default)", badge: "EN", dir: "ltr" },
  { code: "bn", name: "Bangla", nativeName: "বাংলা (Bangla)", badge: "বাং", dir: "ltr" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी (Hindi)", badge: "हिं", dir: "ltr" },
  { code: "ur", name: "Urdu", nativeName: "اردو (Urdu)", badge: "اردو", dir: "rtl" },
  { code: "ar", name: "Arabic", nativeName: "العربية (Arabic)", badge: "عربي", dir: "rtl" },
];

export default function UserHeaderMenu() {
  const { user, openAuthModal, logout } = useAuth();
  const routeParams = useParams();
  const subdomain = (routeParams?.subdomain as string) || "nextlook";

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const [selectedLang, setSelectedLang] = useState<StoreLangCode>("en");
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const [isAppMenuOpen, setIsAppMenuOpen] = useState(false);
  const appDropdownRef = useRef<HTMLDivElement>(null);

  const [isMobileHubOpen, setIsMobileHubOpen] = useState(false);
  const mobileHubRef = useRef<HTMLDivElement>(null);

  const [appConfig, setAppConfig] = useState<any>({
    show_apps_in_navbar: true,
    navbar_app_badge_text: "Download Store App",
    apk_download_url: "",
    ios_download_url: "",
    windows_download_url: "",
    store_app_version: "1.0.0",
  });

  const getApiBase = useCallback(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${subdomain}.localhost:8000`;
      }
    }
    return "";
  }, [subdomain]);

  const loadAppDistribution = useCallback(async () => {
    if (typeof window === "undefined") return;
    try {
      const cached = localStorage.getItem(`store_app_distribution_${subdomain}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        setAppConfig((prev: any) => ({ ...prev, ...parsed }));
      }
    } catch {}

    try {
      const base = getApiBase();
      const res = await fetch(`${base}/api/v1/stores/app-builder/?subdomain=${subdomain}`, {
        headers: { Accept: "application/json", "X-Tenant-Domain": subdomain },
      });
      if (res.ok) {
        const json = await res.json();
        const data = json.app_config || json.data || json;
        if (data && typeof data === "object") {
          setAppConfig((prev: any) => ({ ...prev, ...data }));
        }
      }
    } catch {}
  }, [getApiBase, subdomain]);

  useEffect(() => {
    loadAppDistribution();
    const handleUpdate = (e: any) => {
      if (e?.detail) {
        setAppConfig((prev: any) => ({ ...prev, ...e.detail }));
      } else {
        loadAppDistribution();
      }
    };
    window.addEventListener("storeAppDistributionUpdated", handleUpdate);
    return () => window.removeEventListener("storeAppDistributionUpdated", handleUpdate);
  }, [loadAppDistribution]);

  const base = getApiBase();
  const apkUrl =
    appConfig.apk_download_url ||
    `${base}/api/v1/stores/app-builder/download/apk/?subdomain=${subdomain}`;
  const iosUrl =
    appConfig.ios_download_url ||
    `${base}/api/v1/stores/app-builder/download/ios/?subdomain=${subdomain}`;
  const windowsUrl =
    appConfig.windows_download_url ||
    `${base}/api/v1/stores/app-builder/download/windows/?subdomain=${subdomain}`;

  const currentLangObj =
    STORE_LANGUAGES.find((l) => l.code === selectedLang) || STORE_LANGUAGES[0];

  const setTranslateCookie = useCallback((langCode: StoreLangCode) => {
    if (typeof document === "undefined") return;
    const cookieVal = `/auto/${langCode}`;
    document.cookie = `googtrans=${cookieVal}; path=/; max-age=31536000`;
    const host = window.location.hostname;
    if (host && !host.includes("localhost") && host !== "127.0.0.1") {
      document.cookie = `googtrans=${cookieVal}; path=/; domain=.${host}; max-age=31536000`;
    }
  }, []);

  const applyLanguageToDOM = useCallback(
    (langCode: StoreLangCode) => {
      if (typeof window === "undefined") return;

      setTranslateCookie(langCode);

      const selectEl = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
      if (selectEl) {
        selectEl.value = langCode;
        selectEl.dispatchEvent(new Event("change", { bubbles: true }));
      }
    },
    [setTranslateCookie]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const saved = (localStorage.getItem("storefront_lang") as StoreLangCode) || "en";
    const validSaved = STORE_LANGUAGES.some((l) => l.code === saved) ? saved : "en";
    setSelectedLang(validSaved);
    setTranslateCookie(validSaved);

    (window as any).googleTranslateElementInit = () => {
      if ((window as any).google && (window as any).google.translate) {
        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: "auto",
            includedLanguages: "en,bn,hi,ur,ar",
            autoDisplay: false,
          },
          "xylo_store_translate_container"
        );

        setTimeout(() => {
          applyLanguageToDOM(validSaved);
        }, 400);
      }
    };

    if (!document.getElementById("xylo-neural-translate-script")) {
      const script = document.createElement("script");
      script.id = "xylo-neural-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        applyLanguageToDOM(validSaved);
      }, 300);
    }
  }, [applyLanguageToDOM, setTranslateCookie]);

  const handleSelectLanguage = (code: StoreLangCode) => {
    setSelectedLang(code);
    setIsLangMenuOpen(false);
    setIsMobileHubOpen(false);

    if (typeof window !== "undefined") {
      localStorage.setItem("storefront_lang", code);
      applyLanguageToDOM(code);

      setTimeout(() => {
        applyLanguageToDOM(code);
      }, 250);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
      if (appDropdownRef.current && !appDropdownRef.current.contains(event.target as Node)) {
        setIsAppMenuOpen(false);
      }
      if (mobileHubRef.current && !mobileHubRef.current.contains(event.target as Node)) {
        setIsMobileHubOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fullName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      user.email.split("@")[0]
    : "";

  const initial = fullName ? fullName.charAt(0).toUpperCase() || "U" : "U";

  return (
    <>
      <style jsx global>{`
        .goog-te-banner-frame,
        .goog-te-balloon-frame,
        .goog-te-gadget,
        .goog-te-gadget-icon,
        .goog-te-gadget-simple,
        .goog-te-menu-value,
        #goog-gt-tt,
        .goog-tooltip,
        .goog-tooltip:hover,
        .skiptranslate > iframe,
        iframe.skiptranslate,
        iframe.goog-te-banner-frame,
        body > .skiptranslate,
        [class*="VIpgJd-ZVi9od"],
        [class*="VIpgJd-yAWNEb"],
        [id*="goog-gt-"],
        .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
        .VIpgJd-ZVi9od-aZ2wEe-OiiCO,
        .VIpgJd-ZVi9od-ORHb-OEVmcd,
        .VIpgJd-ZVi9od-l4eHX-hSRGPd {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          width: 0px !important;
          height: 0px !important;
          position: absolute !important;
          left: -9999px !important;
          top: -9999px !important;
          z-index: -9999 !important;
        }
        .goog-text-highlight {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        body {
          top: 0px !important;
          position: static !important;
        }
        #xylo_store_translate_container {
          position: fixed !important;
          bottom: -9999px !important;
          left: -9999px !important;
          width: 1px !important;
          height: 1px !important;
          overflow: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          z-index: -9999 !important;
        }
      `}</style>

      {/* Hidden Neural Translation Mount Point */}
      <div id="xylo_store_translate_container" className="notranslate" aria-hidden="true" />

      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {/* MOBILE UNIFIED SINGLE DROPDOWN BUTTON (App Download & Language) */}
        <div className="relative flex md:hidden notranslate select-none" ref={mobileHubRef}>
          <button
            type="button"
            onClick={() => setIsMobileHubOpen((prev) => !prev)}
            className="px-2.5 py-1.5 rounded-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-200/90 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm active:scale-95"
            title="App Download & Language"
            aria-label="Open Quick Tools Menu"
          >
            <Smartphone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-700 text-[9px] font-black font-mono">
              {currentLangObj.badge}
            </span>
            <ChevronDown
              className={`w-3 h-3 text-slate-500 transition-transform duration-200 ${
                isMobileHubOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isMobileHubOpen && (
            <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-72 bg-white rounded-3xl border border-slate-200 text-slate-800 shadow-2xl p-3 z-[99999] max-h-[82vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150 space-y-3">
              {appConfig.show_apps_in_navbar !== false && (
                <div>
                  <div className="px-1 pb-1.5 flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600">
                      1. {appConfig.navbar_app_badge_text || "Download Store App"}
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-mono font-bold">
                      v{appConfig.store_app_version || "1.0.0"}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5">
                    <a
                      href={apkUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsMobileHubOpen(false)}
                      className="flex flex-col items-center justify-center p-2 rounded-2xl bg-emerald-50/80 hover:bg-emerald-100 text-slate-900 border border-emerald-200/80 transition-all cursor-pointer text-center"
                    >
                      <span className="w-7 h-7 rounded-xl bg-emerald-600 text-white flex items-center justify-center mb-1 shadow-xs">
                        <Smartphone className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-[10px] font-black text-slate-900 leading-none">
                        Android
                      </span>
                      <span className="text-[8px] text-emerald-700 font-bold mt-0.5 flex items-center gap-0.5">
                        APK <Download className="w-2.5 h-2.5" />
                      </span>
                    </a>

                    <a
                      href={iosUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsMobileHubOpen(false)}
                      className="flex flex-col items-center justify-center p-2 rounded-2xl bg-sky-50/80 hover:bg-sky-100 text-slate-900 border border-sky-200/80 transition-all cursor-pointer text-center"
                    >
                      <span className="w-7 h-7 rounded-xl bg-sky-600 text-white flex items-center justify-center mb-1 shadow-xs">
                        <Apple className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-[10px] font-black text-slate-900 leading-none">
                        Apple iOS
                      </span>
                      <span className="text-[8px] text-sky-700 font-bold mt-0.5 flex items-center gap-0.5">
                        iPhone <Download className="w-2.5 h-2.5" />
                      </span>
                    </a>

                    <a
                      href={windowsUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsMobileHubOpen(false)}
                      className="flex flex-col items-center justify-center p-2 rounded-2xl bg-indigo-50/80 hover:bg-indigo-100 text-slate-900 border border-indigo-200/80 transition-all cursor-pointer text-center"
                    >
                      <span className="w-7 h-7 rounded-xl bg-indigo-600 text-white flex items-center justify-center mb-1 shadow-xs">
                        <Monitor className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-[10px] font-black text-slate-900 leading-none">
                        Windows
                      </span>
                      <span className="text-[8px] text-indigo-700 font-bold mt-0.5 flex items-center gap-0.5">
                        .EXE <Download className="w-2.5 h-2.5" />
                      </span>
                    </a>
                  </div>
                </div>
              )}

              <div className="pt-2 border-t border-slate-100">
                <div className="px-1 pb-1.5 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Globe className="w-3 h-3 text-emerald-600" /> 2. Select Language
                  </span>
                  <span className="text-[9px] font-mono text-emerald-600 font-bold">
                    {currentLangObj.name}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-1.5">
                  {STORE_LANGUAGES.map((item) => {
                    const isActive = selectedLang === item.code;
                    return (
                      <button
                        key={item.code}
                        type="button"
                        onClick={() => handleSelectLanguage(item.code)}
                        className={`flex items-center justify-between px-2.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                          isActive
                            ? "bg-emerald-50 border-emerald-500/50 text-slate-950 font-black"
                            : "bg-slate-50/70 border-slate-200/70 text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-black font-mono shrink-0 ${
                              isActive
                                ? "bg-emerald-600 text-white"
                                : "bg-white text-slate-600 border border-slate-200"
                            }`}
                          >
                            {item.badge}
                          </span>
                          <span className="text-[11px] font-extrabold truncate">
                            {item.name}
                          </span>
                        </div>
                        {isActive && (
                          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* DESKTOP BUTTONS (Only Get App & Language Switcher — Visual Search Removed) */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {appConfig.show_apps_in_navbar !== false && (
            <div className="relative select-none" ref={appDropdownRef}>
              <button
                type="button"
                onClick={() => setIsAppMenuOpen((prev) => !prev)}
                className="px-2.5 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
                title="Download Android APK, iOS & Windows App"
                aria-label="Download Store App"
              >
                <Smartphone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="hidden lg:inline-block text-xs font-extrabold text-emerald-900">
                  Get App
                </span>
                <span className="px-1.5 py-0.5 rounded-md bg-emerald-600 text-white text-[9px] font-black font-mono uppercase">
                  APK/iOS
                </span>
                <ChevronDown
                  className={`w-3 h-3 text-emerald-600 transition-transform duration-200 ${
                    isAppMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isAppMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl border border-slate-200 text-slate-800 shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95 duration-150">
                  <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 block">
                        {appConfig.navbar_app_badge_text || "Download Store App"}
                      </span>
                      <span className="text-[9px] text-slate-400 font-medium">
                        Android APK • iOS • Windows
                      </span>
                    </div>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-mono font-bold">
                      v{appConfig.store_app_version || "1.0.0"}
                    </span>
                  </div>

                  <div className="mt-1.5 space-y-1">
                    <a
                      href={apkUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsAppMenuOpen(false)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold bg-emerald-50/70 hover:bg-emerald-100/80 text-slate-900 border border-emerald-200/70 transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                          <Smartphone className="w-3.5 h-3.5" />
                        </span>
                        <div className="text-left leading-tight">
                          <div className="text-xs font-black text-slate-900">Android APK</div>
                          <div className="text-[10px] text-emerald-700 font-semibold">
                            Direct .APK Download
                          </div>
                        </div>
                      </div>
                      <Download className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    </a>

                    <a
                      href={iosUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsAppMenuOpen(false)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold bg-sky-50/70 hover:bg-sky-100/80 text-slate-900 border border-sky-200/70 transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-sky-600 text-white flex items-center justify-center">
                          <Apple className="w-3.5 h-3.5" />
                        </span>
                        <div className="text-left leading-tight">
                          <div className="text-xs font-black text-slate-900">Apple iOS App</div>
                          <div className="text-[10px] text-sky-700 font-semibold">
                            iPhone & iPad (.ipa)
                          </div>
                        </div>
                      </div>
                      <Download className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                    </a>

                    <a
                      href={windowsUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setIsAppMenuOpen(false)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold bg-indigo-50/70 hover:bg-indigo-100/80 text-slate-900 border border-indigo-200/70 transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
                          <Monitor className="w-3.5 h-3.5" />
                        </span>
                        <div className="text-left leading-tight">
                          <div className="text-xs font-black text-slate-900">Windows Desktop</div>
                          <div className="text-[10px] text-indigo-700 font-semibold">
                            PC Installer (.exe)
                          </div>
                        </div>
                      </div>
                      <Download className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="relative notranslate select-none" ref={langDropdownRef}>
            <button
              type="button"
              onClick={() => setIsLangMenuOpen((prev) => !prev)}
              className="px-2.5 py-1.5 rounded-full bg-slate-100/90 hover:bg-slate-200/80 text-slate-700 border border-slate-200 transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
              title="Change Store Language"
              aria-label="Change Language"
            >
              <Globe className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="hidden lg:inline-block text-xs font-extrabold text-slate-800">
                {currentLangObj.name}
              </span>
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-700 text-[10px] font-black font-mono">
                {currentLangObj.badge}
              </span>
              <ChevronDown
                className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                  isLangMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isLangMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-slate-200 text-slate-800 shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                <div className="px-3 py-1.5 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    Select Language
                  </span>
                  <span className="text-[9px] font-mono text-emerald-600 font-bold">
                    5 Languages
                  </span>
                </div>

                <div className="mt-1 space-y-0.5">
                  {STORE_LANGUAGES.map((item) => {
                    const isActive = selectedLang === item.code;
                    return (
                      <button
                        key={item.code}
                        type="button"
                        onClick={() => handleSelectLanguage(item.code)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                          isActive
                            ? "bg-emerald-50 border-emerald-500/40 text-slate-950 font-black"
                            : "border-transparent text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`w-8 h-6 rounded-lg flex items-center justify-center text-[10px] font-black font-mono ${
                              isActive
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {item.badge}
                          </span>
                          <div className="text-left leading-tight">
                            <div className="text-xs font-extrabold">{item.name}</div>
                            <div className="text-[10px] text-slate-400 font-medium">
                              {item.nativeName}
                            </div>
                          </div>
                        </div>

                        {isActive && (
                          <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Profile Menu */}
        {user ? (
          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              title={fullName}
              aria-label="Open user menu"
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold text-xs transition-all active:scale-95 cursor-pointer shadow-2xs select-none"
            >
              {initial}
            </button>

            {isOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 sm:w-64 bg-white rounded-2xl shadow-xl border border-slate-100 p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="p-2.5 bg-slate-50/80 rounded-xl mb-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-full bg-emerald-600 text-white font-bold text-xs flex items-center justify-center shrink-0">
                      {initial}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-900 truncate">
                        {fullName}
                      </p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100/60 px-2 py-0.5 rounded-full">
                    <Sparkles className="w-2.5 h-2.5" />
                    Verified Customer
                  </span>
                </div>

                <Link
                  href="/dashboard"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/70 rounded-xl transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4 text-emerald-600" />
                  <span>Dashboard</span>
                </Link>

                <div className="my-1 border-t border-slate-100" />

                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer"
                >
                  <LogOut className="w-4 h-4 text-rose-500" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openAuthModal("login")}
            aria-label="Sign In"
            title="Sign In"
            className="p-1.5 sm:px-3 sm:py-1.5 rounded-full hover:bg-slate-100 text-slate-700 transition-all cursor-pointer flex items-center gap-1.5 shrink-0 border border-transparent hover:border-slate-200"
          >
            <UserIcon className="w-5 h-5 text-slate-600 shrink-0" />
            <span className="text-xs font-bold hidden sm:inline leading-none">
              Sign In
            </span>
          </button>
        )}
      </div>
    </>
  );
}